import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { tournamentsAPI, getToken } from '../api/api'

// ─── Firebase (Auth only) ─────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PLATFORM_FEE = 0.15
const API_PAGE_SIZE = 20

function calcPrizes(entryFee, maxPlayers, prizeMode) {
    const gross = entryFee * maxPlayers
    const net   = Math.floor(gross * (1 - PLATFORM_FEE))
    if (prizeMode === 'winner') return { 1: net }
    return {
        1: Math.floor(net * 0.50),
        2: Math.floor(net * 0.30),
        3: Math.floor(net * 0.20),
    }
}

// MongoDB status → display status
function mongoStatusToDisplay(s) {
    const map = { open: 'waiting', locked: 'inprogress', active: 'inprogress', settled: 'settled', disputed: 'disputed', cancelled: 'cancelled' }
    return map[s] || s
}

function statusInfo(status) {
    switch (status) {
        case 'waiting':    return { label: 'Open',       color: '#4ade80', bg: '#0a2a0c' }
        case 'inprogress': return { label: 'Live',       color: '#fbbf24', bg: '#2a1a00' }
        case 'announced':  return { label: 'Announcing', color: '#60a5fa', bg: '#0a1a2a' }
        case 'settled':    return { label: 'Settled',    color: '#9ca3af', bg: '#1a1a1a' }
        case 'cancelled':  return { label: 'Cancelled',  color: '#f87171', bg: '#2a0a0a' }
        case 'disputed':   return { label: 'Disputed',   color: '#f97316', bg: '#2a1000' }
        default:           return { label: status || '?',color: '#9ca3af', bg: '#1a1a1a' }
    }
}

function timeAgo(ts) {
    if (!ts) return ''
    const diff = Date.now() - ts
    const m = Math.floor(diff / 60000)
    if (m < 1) return 'just now'
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    return `${Math.floor(h / 24)}d ago`
}

function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function TournamentList() {
    const navigate = useNavigate()

    const [currentUser, setCurrentUser] = useState(null)
    const [tournaments, setTournaments] = useState([])
    const [loading, setLoading]         = useState(true)
    const [activeTab, setActiveTab]     = useState('open')  // open | all | mine
    const [searchId, setSearchId]           = useState('')
    const [error, setError]             = useState('')
    const [total, setTotal]             = useState(0)

    // ── Auth ──────────────────────────────────────────────────
    useEffect(() => {
        const app  = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        const auth = getAuth(app)
        const unsub = onAuthStateChanged(auth, user => setCurrentUser(user))
        return () => unsub()
    }, [])

    // ── Load tournaments from MongoDB ─────────────────────────
    const load = useCallback(async (tab, pageSkip) => {
        if (!getToken()) { setError('Please log in to view tournaments.'); setLoading(false); return }
        setLoading(true)
        setError('')
        try {
            let result
            if (tab === 'mine') {
                result = await tournamentsAPI.mine(API_PAGE_SIZE, pageSkip)
            } else {
                // 'open' → status=open (waiting lobby), 'all' → no status filter
                const statusFilter = tab === 'open' ? 'open' : null
                result = await tournamentsAPI.list(statusFilter, null, API_PAGE_SIZE, pageSkip)
            }

            const list = (result.tournaments || []).map(t => ({
                tid:         t._id,
                mongoId:     t._id,
                name:        t.title || 'Tournament',
                gameType:    t.game || 'pool',
                hostUid:     t.host?._id || '',
                hostName:    t.host?.username || 'Host',
                entryFee:    t.entryFee || 0,
                maxPlayers:  t.maxPlayers || 8,
                prizeMode:   t.prizeMode === 'winner_takes_all' ? 'winner' : 'top3',
                status:      mongoStatusToDisplay(t.status),
                createdAt:   new Date(t.createdAt).getTime(),
                playerCount: t.players?.length || 0,
                players:     {},
            }))

            setTournaments(pageSkip === 0 ? list : prev => [...prev, ...list])
            setTotal(result.total || 0)
        } catch (e) {
            setError(e.message || 'Failed to load tournaments.')
        }
        setLoading(false)
    }, [])

    // ── Load on mount and when the active tab changes ─────────
    useEffect(() => {
        load(activeTab, 0)
    }, [activeTab, load])

    const hasMore = tournaments.length < total
    const handleLoadMore = useCallback(() => {
        load(activeTab, tournaments.length)
    }, [load, activeTab, tournaments.length])

    // ── Filtered list ─────────────────────────────────────────
    const SHOW_AGE_MS = 48 * 60 * 60 * 1000  // hide settled/cancelled after 48h
    const filtered = tournaments.filter(t => {
        // Search by ClubSwap ID
        if (searchId.trim() && !t.tid.toLowerCase().includes(searchId.trim().toLowerCase())) return false
        // Always hide very old settled/cancelled/expired
        const isFinished = ['settled', 'cancelled', 'refunded'].includes(t.status)
        const isTooOld = (Date.now() - (t.createdAt || 0)) > SHOW_AGE_MS
        if (isFinished && isTooOld) return false

        if (activeTab === 'open') return ['waiting', 'inprogress'].includes(t.status)
        if (activeTab === 'mine') return currentUser && (
            t.hostUid === currentUser.uid ||
            !!t.players[currentUser.uid]
        )
        return true  // 'all'
    })

    // ─────────────────────────────────────────────────────────
    //  RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div style={s.root}>
            <div style={s.page}>

                {/* Header */}
                <div style={s.header}>
                    <div>
                        <div style={s.title}>Tournaments</div>
                        <div style={s.sub}>Join a lobby, deposit your diamonds, play to win</div>
                    </div>
                    <button style={s.createBtn} onClick={() => navigate('/tournament')}>
                        + Create
                    </button>
                </div>

                {/* Tabs */}
                <div style={s.tabs}>
                    {[
                        { id: 'open', label: 'Open' },
                        { id: 'all',  label: 'All'  },
                        { id: 'mine', label: 'My Tournaments' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            style={activeTab === tab.id ? s.tabActive : s.tab}
                            onClick={() => setActiveTab(tab.id)}
                        >{tab.label}</button>
                    ))}
                </div>

                {/* Content */}
                {error && <div style={s.errorBanner}>{error}</div>}

                {loading ? (
                    <div style={s.empty}>
                        <div className="cs-spinner" style={s.spinner} />
                    </div>
                ) : tournaments.length === 0 ? (
                    <div style={s.empty}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>🏆</div>
                        <div style={s.emptyText}>
                            {activeTab === 'open'
                                ? 'No open tournaments right now.'
                                : activeTab === 'mine'
                                    ? "You haven't joined any tournaments yet."
                                    : 'No tournaments found.'}
                        </div>
                        <button style={s.createBtn2} onClick={() => navigate('/tournament')}>
                            + Create Tournament
                        </button>
                    </div>
                ) : (
                    <>
                        {tournaments.map(t => (
                            <TournamentCard key={t.tid} t={t} currentUser={currentUser} navigate={navigate} />
                        ))}
                        {hasMore && (
                            <button
                                style={s.loadMoreBtn}
                                onClick={handleLoadMore}
                                disabled={loading}
                            >
                                {loading ? 'Loading…' : `Load more (${total - tournaments.length} remaining)`}
                            </button>
                        )}
                    </>
                )}

            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  TOURNAMENT CARD
// ─────────────────────────────────────────────────────────────────────────────
function TournamentCard({ t, currentUser, navigate }) {
    const st      = statusInfo(t.status)
    const prizes  = calcPrizes(t.entryFee, t.maxPlayers, t.prizeMode)
    const fillPct = Math.min(100, Math.round((t.playerCount / t.maxPlayers) * 100))
    const isHost  = currentUser && t.hostUid === currentUser.uid
    const canJoin = t.status === 'waiting' && !isHost && t.playerCount < t.maxPlayers
    const isLive  = t.status === 'waiting' || t.status === 'inprogress'

    return (
        <div style={s.card} onClick={() => navigate(`/tournament/${t.tid}`)}>

            {/* Top row */}
            <div style={s.cardTop}>
                <div style={s.cardIcon}>
                    {t.gameType === 'pool' ? '🎱' : '♟️'}
                </div>
                <div style={s.cardInfo}>
                    <div style={s.cardName}>{t.name}</div>
                    <div style={s.cardMeta}>
                        <span>hosted by {isHost ? 'you' : (t.hostName || 'unknown')}</span>
                        <span style={s.dot}>·</span>
                        <span>{timeAgo(t.createdAt)}</span>
                    </div>
                </div>
                <div style={{ ...s.statusBadge, color: st.color, background: st.bg }}>
                    <span
                        className={isLive ? 'cs-pulse-dot' : undefined}
                        style={{ ...s.statusDot, background: st.color }}
                    />
                    {st.label}
                </div>
            </div>

            {/* Stats row */}
            <div style={s.statsRow}>
                <div style={s.stat}>
                    <div style={s.statVal}>💎 {t.entryFee}</div>
                    <div style={s.statLabel}>Entry fee</div>
                </div>
                <div style={s.stat}>
                    <div style={s.statVal}>{t.playerCount} / {t.maxPlayers}</div>
                    <div style={s.statLabel}>Players</div>
                </div>
                <div style={s.stat}>
                    <div style={s.statVal}>{t.prizeMode === 'winner' ? 'Winner All' : 'Top 3'}</div>
                    <div style={s.statLabel}>Prize split</div>
                </div>
                <div style={s.stat}>
                    <div style={s.statVal}>5%</div>
                    <div style={s.statLabel}>Host bonus</div>
                </div>
            </div>

            {/* Progress bar */}
            <div style={s.progressTrack}>
                <div style={{ ...s.progressFill, width: `${fillPct}%`, background: fillPct >= 100 ? '#ffd700' : '#4ade80' }} />
            </div>
            <div style={s.spotsText}>{t.playerCount} of {t.maxPlayers} spots filled</div>

            {/* Prize breakdown */}
            <div style={s.prizeRow}>
                <span style={s.prizeLabel}>Prize pool ({t.maxPlayers} players)</span>
                <div style={s.prizes}>
                    {Object.entries(prizes).map(([pos, amt]) => (
                        <span key={pos} style={s.prizeChip}>
                            {pos === '1' ? '🥇' : pos === '2' ? '🥈' : '🥉'}{amt}
                        </span>
                    ))}
                </div>
            </div>

            {/* Action button */}
            {isHost ? (
                <div style={s.joinedBtn}>👑 You are the host — View Lobby</div>
            ) : canJoin ? (
                <div style={s.joinBtn}>Join &amp; deposit 💎 {t.entryFee}</div>
            ) : t.status !== 'waiting' ? (
                <div style={s.viewBtn}>View Tournament →</div>
            ) : (
                <div style={s.fullBtn}>Lobby Full</div>
            )}
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s = {
    root: { minHeight: '100vh', background: '#070a0f', color: '#e8f5e9', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    page: { maxWidth: 600, margin: '0 auto', padding: '0 16px 100px' },

    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 0 16px' },
    title: { fontSize: 22, fontWeight: 800, color: '#e8f5e9' },
    sub: { fontSize: 13, color: '#4a7aaa', marginTop: 2 },
    createBtn: { padding: '9px 18px', borderRadius: 10, background: '#4ade80', border: 'none', color: '#070a0f', fontWeight: 800, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
    createBtn2: { padding: '11px 24px', borderRadius: 10, background: '#4ade80', border: 'none', color: '#070a0f', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginTop: 12 },

    tabs: { display: 'flex', gap: 0, background: '#0d1520', borderRadius: 10, padding: 4, marginBottom: 16 },
    tab: { flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: 'transparent', color: '#4a7aaa', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'center' },
    tabActive: { flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#1a2a3a', color: '#e8f5e9', fontSize: 13, fontWeight: 700, cursor: 'pointer', textAlign: 'center' },

    empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' },
    emptyText: { fontSize: 14, color: '#4a7aaa', marginBottom: 8 },
    // Animation applied via className="cs-spinner" — @keyframes spin defined in App.css
    spinner: { width: 32, height: 32, borderRadius: '50%', border: '3px solid #1a2a3a', borderTop: '3px solid #4ade80', animation: 'spin 0.8s linear infinite' },
    errorBanner: { background: '#2a0a0a', border: '1px solid #f44336', borderRadius: 10, padding: '12px 16px', color: '#f87171', fontSize: 13, marginBottom: 4 },
    loadMoreBtn: { width: '100%', padding: '14px', borderRadius: 10, background: '#0d1520', border: '1px solid #1a2a3a', color: '#4a9aca', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginTop: 4 },

    card: { background: '#0d1520', border: '1px solid #1a2a3a', borderRadius: 14, padding: '16px', marginBottom: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 },
    cardTop: { display: 'flex', alignItems: 'flex-start', gap: 12 },
    cardIcon: { fontSize: 28, width: 44, height: 44, background: '#1a2a3a', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardInfo: { flex: 1, minWidth: 0 },
    cardName: { fontSize: 15, fontWeight: 700, color: '#e8f5e9', marginBottom: 2 },
    cardMeta: { fontSize: 12, color: '#4a7aaa' },
    dot: { margin: '0 4px' },

    statusBadge: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 },
    // Pulse animation applied via className="cs-pulse-dot" — @keyframes pulse-dot defined in App.css
    statusDot: { width: 6, height: 6, borderRadius: '50%', display: 'inline-block' },

    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
    stat: { textAlign: 'center', background: '#071018', borderRadius: 8, padding: '8px 4px' },
    statVal: { fontSize: 13, fontWeight: 700, color: '#e8f5e9', marginBottom: 2 },
    statLabel: { fontSize: 10, color: '#4a6a8a' },

    progressTrack: { height: 4, background: '#1a2a3a', borderRadius: 2, overflow: 'hidden' },
    progressFill: { height: '100%', borderRadius: 2, transition: 'width 0.3s' },
    spotsText: { fontSize: 11, color: '#4a6a8a', textAlign: 'right' },

    prizeRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 },
    prizeLabel: { fontSize: 11, color: '#4a6a8a' },
    prizes: { display: 'flex', gap: 6 },
    prizeChip: { fontSize: 12, fontWeight: 700, color: '#ffd700', background: '#1a1a00', padding: '3px 8px', borderRadius: 8 },

    joinBtn: { background: '#4ade80', color: '#070a0f', fontWeight: 800, fontSize: 14, padding: '12px', borderRadius: 10, textAlign: 'center' },
    joinedBtn: { background: '#0a2a0c', border: '1px solid #2a5a2c', color: '#4ade80', fontWeight: 700, fontSize: 13, padding: '11px', borderRadius: 10, textAlign: 'center' },
    viewBtn: { background: '#1a2a3a', color: '#4a9aca', fontWeight: 600, fontSize: 13, padding: '11px', borderRadius: 10, textAlign: 'center' },
    fullBtn: { background: '#1a1a1a', color: '#4a4a4a', fontWeight: 600, fontSize: 13, padding: '11px', borderRadius: 10, textAlign: 'center' },
}
